import type { GameModeKey } from '../config/GameConfig';

/**
 * 永久最高分（localStorage 持久化）—— 按模式分桶：
 *   - winle.highScore           历史遗留 key，关卡模式 + 无尽模式 都没明确指定时回退到这个
 *   - winle.highScore.campaign  关卡模式
 *   - winle.highScore.endless   无尽模式
 *
 * 历史 key 仍保留读，避免老用户最高分清零。
 */
const LEGACY_KEY = 'winle.highScore';
const KEY_PREFIX = 'winle.highScore.';

function _scoreKey(mode?: GameModeKey): string {
  return mode ? KEY_PREFIX + mode : LEGACY_KEY;
}

export function loadHighScore(mode?: GameModeKey): number {
  try {
    const raw = localStorage.getItem(_scoreKey(mode));
    // 关卡 / 无尽 第一次读时若没有专属记录，回退到老 key（继承遗留分数）
    const fallback = mode && raw == null ? localStorage.getItem(LEGACY_KEY) : null;
    const v = parseInt(raw ?? fallback ?? '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

/** 仅在 score 大于已存储值时写入；返回写入后的值 */
export function saveHighScore(score: number, mode?: GameModeKey): number {
  try {
    const cur = loadHighScore(mode);
    if (score > cur) {
      localStorage.setItem(_scoreKey(mode), String(score));
      return score;
    }
    return cur;
  } catch {
    return score;
  }
}

export function resetHighScore(mode?: GameModeKey): void {
  try {
    if (mode) {
      localStorage.removeItem(_scoreKey(mode));
    } else {
      // 不传 mode 时全清（菜单上的"重置"按钮）
      localStorage.removeItem(LEGACY_KEY);
      localStorage.removeItem(KEY_PREFIX + 'campaign');
      localStorage.removeItem(KEY_PREFIX + 'endless');
      localStorage.removeItem(CLEARS_KEY);
    }
  } catch { /* ignore */ }
}

// ─── 通关次数（仅 campaign） ─────────────────────────────────

const CLEARS_KEY = 'winle.campaignClears';

export function loadCampaignClears(): number {
  try {
    const v = parseInt(localStorage.getItem(CLEARS_KEY) || '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

export function incCampaignClears(): number {
  try {
    const n = loadCampaignClears() + 1;
    localStorage.setItem(CLEARS_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

// ─── 首次引导提示标记 ────────────────────────────────────────
//
// 给"撞 3 个宝箱可升级"等一次性 toast 用。
// 第一次开局把 KEY 设为 '1'，下次进游戏就跳过提示。

const HINTS_KEY = 'winle.seenHints';

type SeenHints = Record<string, true>;

function _loadHints(): SeenHints {
  try {
    return JSON.parse(localStorage.getItem(HINTS_KEY) || '{}') as SeenHints;
  } catch {
    return {};
  }
}

export function hasSeenHint(id: string): boolean {
  return !!_loadHints()[id];
}

export function markHintSeen(id: string): void {
  try {
    const cur = _loadHints();
    cur[id] = true;
    localStorage.setItem(HINTS_KEY, JSON.stringify(cur));
  } catch {
    /* ignore */
  }
}
